import React, { useRef } from 'react';
import { useFragment, graphql } from 'react-relay';
import { Text } from '@atlaskit/primitives/compiled';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import { EmptyFieldText } from '@atlassian/jira-issue-empty-field-text/src/ui/index.tsx';
import { HoverPopover } from '@atlassian/jira-hover-popover/src/ui/index.tsx';
import { formatNumberField } from '@atlassian/jira-platform-utils-number/src/index.tsx';
import { useBespokeEmptyFieldTextExperiment } from '@atlassian/jira-issue-experiments/src/bespoke-empty-field-text-experiment/index.tsx';
import type { number_issueFieldNumberReadviewFull_NumberReadView$key as NumberFragment } from '@atlassian/jira-relay/src/__generated__/number_issueFieldNumberReadviewFull_NumberReadView.graphql';
import { fg } from '@atlassian/jira-feature-gating';
import type { Props } from './types.tsx';
import messages from './messages.tsx';

export const NumberReadView = ({ fragmentRef, isSingleRowHeightEnabled = false }: Props) => {
	const { formatNumber, formatMessage } = useIntl();
	const noValueTextExperiment = formatMessage(messages.noValueTextExperiment);
	const contentRef = useRef<HTMLElement | null>(null);
	const locale = useLocale();
	const { isExperimentEnabled } = useBespokeEmptyFieldTextExperiment();

	const fieldData = useFragment<NumberFragment>(
		graphql`
			fragment number_issueFieldNumberReadviewFull_NumberReadView on JiraNumberField
			@argumentDefinitions(
				isNumberFieldConfigurableEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jira-number-field-configuration-enabled.relayprovider"
				}
			) {
				number
				formatConfig @include(if: $isNumberFieldConfigurableEnabled) {
					formatStyle
					formatUnit
				}
			}
		`,
		fragmentRef,
	);

	const { number, formatConfig } = fieldData;
	const children =
		// eslint-disable-next-line no-nested-ternary
		number === null || number === undefined ? (
			<EmptyFieldText emptyText={isExperimentEnabled ? noValueTextExperiment : undefined} />
		) : formatConfig && formatConfig.formatStyle && fg('frontier-currency-field-d1') ? (
			formatNumberField(number ?? 0, {
				formatStyle: formatConfig.formatStyle,
				formatUnit: formatConfig.formatUnit,
				locale,
			})
		) : (
			formatNumber(number)
		);

	if (isSingleRowHeightEnabled) {
		return (
			<HoverPopover content={formatNumber(number ?? 0)} contentRef={contentRef}>
				{({ ref }) => (
					<Text
						testId="issue-field-number-readview-full.ui.number.text"
						ref={mergeRefs(contentRef, ref)}
						maxLines={1}
					>
						{children}
					</Text>
				)}
			</HoverPopover>
		);
	}

	return <span data-testid="issue-field-number-readview-full.ui.number.span">{children}</span>;
};
